// src/contexts/DemoContext.jsx — `useDemo()`: is this the trailer, which scene
// is the visitor on, and the rail's next/prev. Inert outside demo mode: every
// value is false/null and the provider renders children unchanged.
import { createContext, useContext, useMemo, useCallback, useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { IS_DEMO, EIGHTY_DAYS_STUDIO_READY, readDemoSessionId, recordDemoEvent } from '../lib/demoClient'
import { readyScenes, sceneIndexFor, sceneAt } from '../lib/demoScenes'
import { demoSessionAlive, recoverDemoSession } from '../lib/demoRecovery'

const DemoContext = createContext({
  isDemo: false, sessionId: null, scenes: [], sceneIndex: -1, scene: null,
  goTo: () => {}, next: () => {}, prev: () => {}, leave: () => {},
})

// The scenes this deploy may actually route a visitor to — drops the three
// Studio-only scenes unless EIGHTY_DAYS_STUDIO_READY says the pictures are
// live (see demoClient.js and demoScenes.js's own STUDIO_GATED_KEYS).
// Module-level: the flag never changes at runtime, so this needs computing
// exactly once, not per render.
const ACTIVE_SCENES = readyScenes(EIGHTY_DAYS_STUDIO_READY)

export function DemoProvider({ children }) {
  const location = useLocation()
  const navigate = useNavigate()
  const sceneIndex = IS_DEMO ? sceneIndexFor(location.pathname, location.search, ACTIVE_SCENES) : -1
  const scene = sceneIndex >= 0 ? ACTIVE_SCENES[sceneIndex] : null
  const lastScene = useRef(null)

  useEffect(() => {
    if (!IS_DEMO) return
    const key = scene?.key || null
    if (key === lastScene.current) return
    if (lastScene.current) recordDemoEvent('scene_leave', lastScene.current)
    if (key) recordDemoEvent('scene_enter', key)
    lastScene.current = key
  }, [scene?.key])

  // A tab left open can outlive its session (a reseed, a sign-out elsewhere).
  // Check when the trailer mounts and whenever the visitor comes back to the
  // tab; a definite refusal re-enters through /demo instead of an error page.
  useEffect(() => {
    if (!IS_DEMO) return
    const check = () => {
      if (document.visibilityState !== 'visible' || window.location.pathname === '/demo') return
      demoSessionAlive().then((ok) => { if (!ok) recoverDemoSession() }).catch(() => {})
    }
    check()
    document.addEventListener('visibilitychange', check)
    return () => document.removeEventListener('visibilitychange', check)
  }, [])

  const goTo = useCallback((i) => { navigate(sceneAt(i, ACTIVE_SCENES).path) }, [navigate])
  const next = useCallback(() => {
    if (sceneIndex >= ACTIVE_SCENES.length - 1) { recordDemoEvent('rail_finish', scene?.key); return }
    goTo(sceneIndex + 1)
  }, [sceneIndex, scene?.key, goTo])
  const prev = useCallback(() => goTo(Math.max(0, sceneIndex - 1)), [sceneIndex, goTo])
  const leave = useCallback(() => { recordDemoEvent('rail_leave', scene?.key) }, [scene?.key])

  const value = useMemo(() => ({
    isDemo: IS_DEMO,
    sessionId: IS_DEMO ? readDemoSessionId() : null,
    scenes: IS_DEMO ? ACTIVE_SCENES : [],
    sceneIndex, scene, goTo, next, prev, leave,
  }), [sceneIndex, scene, goTo, next, prev, leave])

  return <DemoContext.Provider value={value}>{children}</DemoContext.Provider>
}

export const useDemo = () => useContext(DemoContext)
